import { useEffect, useMemo, useRef } from 'react';
import { useShallow } from 'zustand/shallow';

import { type GetArticlesBySettingTypeParams, type GetArticlesBySystemParams, SettingModelSettingType } from 'api/models';
import { useGetArticlesBySettingType, useGetArticlesBySystem } from 'api/queries/articles/articles';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

interface useGetArticleDataProps {
    settingType: SettingModelSettingType | undefined;
}

export const useGetArticleData = ({ settingType }: useGetArticleDataProps) => {
    const { isPPMFilterActive, batchAddArticles } = usePlacementStore(
        useShallow((state) => ({
            isPPMFilterActive: state.isPPMFilterActive,
            batchAddArticles: state.batchAddArticles
        }))
    );

    const articlesCountRef = useRef<number>(0);
    const queryKey = useMemo(
        () => [`settings - filterActive: ' ${isPPMFilterActive}`, settingType],
        [isPPMFilterActive, settingType]
    );

    const allArticleDataParams: GetArticlesBySystemParams = useMemo(
        () => ({
            filterActive: isPPMFilterActive
        }),
        [isPPMFilterActive]
    );

    const { data: allArticleData, isPending: isAllArticleDataPending } = useGetArticlesBySystem(
        import.meta.env.VITE_DEFAULT_SYSTEM_ID,
        allArticleDataParams,
        {
            query: {
                queryKey,
                enabled: settingType === undefined,
                staleTime: 24 * 60 * 60 * 1000
            }
        }
    );

    // TO DO: get system info from Revit or implement system selection
    const filteredArticleDataParams: GetArticlesBySettingTypeParams = useMemo(
        () => ({
            systemId: import.meta.env.VITE_DEFAULT_SYSTEM_ID,
            settingType: settingType as SettingModelSettingType,
            filterActive: isPPMFilterActive
        }),
        [isPPMFilterActive, settingType]
    );

    const { data: filteredArticleData, isPending: isFilteredArticleDataPending } = useGetArticlesBySettingType(
        filteredArticleDataParams,
        {
            query: {
                queryKey,
                enabled: settingType !== undefined,
                staleTime: 24 * 60 * 60 * 1000
            }
        }
    );

    const isArticleDataPending = settingType === undefined ? isAllArticleDataPending : isFilteredArticleDataPending;

    const articleData = useMemo(() => {
        return settingType === undefined ? (allArticleData?.data ?? []) : (filteredArticleData?.data ?? []);
    }, [settingType, allArticleData?.data, filteredArticleData?.data]);

    useEffect(() => {
        // neccessary: cannot access ref during render in the usememo hook
        if (!articleData || articleData.length === 0) {
            return;
        }

        if (articlesCountRef.current === 0) {
            const entries = articleData
                .filter((article) => !!article)
                .map((article) => ({
                    id: article.id,
                    articleNumber: article.articleNumber,
                    lifeCycleCode: article.lifeCycleCode
                }));
            batchAddArticles(entries);
        }
    }, [batchAddArticles, articleData]);

    useEffect(() => {
        const subscribedArticlesCount = usePlacementStore.subscribe(
            (state) => Object.keys(state.articles).length,
            (newCount) => {
                articlesCountRef.current = newCount;
            }
        );

        return () => {
            subscribedArticlesCount();
        };
    }, []);

    return { articleData, isArticleDataPending };
};
